import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  Typography,
  Button,
  Spinner,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  TabsHeader,
  Tab,
  TabsBody,
  TabPanel,
} from '@material-tailwind/react';
import {
  PencilIcon,
  ArrowPathIcon,
  DocumentPlusIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

import { listDrafts } from '../../services/reportApi';
import { UserAuth } from '../../context/AuthContext';

const STATUS_COLORS = {
  draft: 'gray',
  pending_review: 'amber',
  finalizing: 'blue',
  finalized: 'green',
  error: 'red',
};

const STATUS_LABELS = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  finalizing: 'Processing',
  finalized: 'Finalized',
  error: 'Error',
};

function DraftsListPage() {
  const { user } = UserAuth();
  const [ownDrafts, setOwnDrafts] = useState([]);
  const [teamDrafts, setTeamDrafts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('own');
  const navigate = useNavigate();

  const loadDrafts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await listDrafts();

      // Handle { own: [...], team: [...] } response format
      if (data && typeof data === 'object') {
        setOwnDrafts(Array.isArray(data.own) ? data.own : []);
        setTeamDrafts(Array.isArray(data.team) ? data.team : []);
      } else if (Array.isArray(data)) {
        // Fallback for flat array response
        setOwnDrafts(data);
        setTeamDrafts([]);
      } else {
        console.warn('Unexpected drafts response format:', data);
        setOwnDrafts([]);
        setTeamDrafts([]);
      }
    } catch (err) {
      console.error('Failed to load drafts:', err);
      setError(err.message);
      setOwnDrafts([]);
      setTeamDrafts([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (user?.uid) {
      loadDrafts();
    }
  }, [user?.uid]);

  const handleEditDraft = (draftId) => {
    navigate(`/dashboard/report-editor/${draftId}`);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const DraftsTable = ({ drafts, showPreparer = false }) => {
    if (drafts.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500">
          <Typography>No drafts found</Typography>
          {!showPreparer && (
            <Button
              variant="text"
              className="mt-2"
              onClick={() => navigate('/dashboard/report-editor')}
            >
              Create your first draft
            </Button>
          )}
        </div>
      );
    }

    return (
      <table className="w-full min-w-max table-auto text-left">
        <thead>
          <tr>
            <th className="border-b border-gray-200 bg-gray-50 p-4">
              <Typography variant="small" className="font-semibold text-gray-600">
                Customer
              </Typography>
            </th>
            <th className="border-b border-gray-200 bg-gray-50 p-4">
              <Typography variant="small" className="font-semibold text-gray-600">
                Aircraft
              </Typography>
            </th>
            {showPreparer && (
              <th className="border-b border-gray-200 bg-gray-50 p-4">
                <Typography variant="small" className="font-semibold text-gray-600">
                  Preparer
                </Typography>
              </th>
            )}
            <th className="border-b border-gray-200 bg-gray-50 p-4">
              <Typography variant="small" className="font-semibold text-gray-600">
                Status
              </Typography>
            </th>
            <th className="border-b border-gray-200 bg-gray-50 p-4">
              <Typography variant="small" className="font-semibold text-gray-600">
                Last Updated
              </Typography>
            </th>
            <th className="border-b border-gray-200 bg-gray-50 p-4">
              <Typography variant="small" className="font-semibold text-gray-600">
                Actions
              </Typography>
            </th>
          </tr>
        </thead>
        <tbody>
          {drafts.map((draft, index) => {
            const isLast = index === drafts.length - 1;
            const rowClass = isLast ? 'p-4' : 'p-4 border-b border-gray-100';
            const draftId = draft.draftId || draft.id;
            const canEdit = draft.status === 'draft' || draft.status === 'pending_review';

            return (
              <tr
                key={draftId}
                className={`hover:bg-gray-50 ${canEdit ? 'cursor-pointer' : ''}`}
                onClick={() => canEdit && handleEditDraft(draftId)}
              >
                <td className={rowClass}>
                  <Typography variant="small" className="font-medium">
                    {draft.customerName || '-'}
                  </Typography>
                </td>
                <td className={rowClass}>
                  <div>
                    <Typography variant="small" className="font-medium">
                      {draft.aircraftModel || '-'}
                    </Typography>
                    {draft.aircraftId && (
                      <Typography variant="small" className="text-gray-500">
                        {draft.aircraftId}
                      </Typography>
                    )}
                  </div>
                </td>
                {showPreparer && (
                  <td className={rowClass}>
                    <Typography variant="small" className="font-medium">
                      {draft.preparer?.name || '-'}
                    </Typography>
                    {draft.preparer?.email && (
                      <Typography variant="small" className="text-gray-500">
                        {draft.preparer.email}
                      </Typography>
                    )}
                  </td>
                )}
                <td className={rowClass}>
                  <Chip
                    size="sm"
                    value={STATUS_LABELS[draft.status] || draft.status}
                    color={STATUS_COLORS[draft.status] || 'gray'}
                  />
                </td>
                <td className={rowClass}>
                  <Typography variant="small" className="text-gray-600">
                    {formatDate(draft.updatedAt || draft.createdAt)}
                  </Typography>
                </td>
                <td className={rowClass} onClick={(e) => e.stopPropagation()}>
                  {canEdit && (
                    <Tooltip content="Edit Draft">
                      <IconButton
                        variant="text"
                        size="sm"
                        onClick={() => handleEditDraft(draftId)}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </IconButton>
                    </Tooltip>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <Typography variant="h4" color="blue-gray">
          Drafts
        </Typography>
        <div className="flex gap-2">
          <Tooltip content="Refresh">
            <IconButton variant="outlined" onClick={loadDrafts} disabled={isLoading}>
              <ArrowPathIcon className="h-4 w-4" />
            </IconButton>
          </Tooltip>
          <Button
            className="flex items-center gap-2"
            onClick={() => navigate('/dashboard/report-editor')}
          >
            <DocumentPlusIcon className="h-5 w-5" />
            New Draft
          </Button>
        </div>
      </div>

      {error && (
        <Alert color="red" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardBody className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="h-8 w-8" />
            </div>
          ) : (
            <Tabs value={activeTab} onChange={(val) => setActiveTab(val)}>
              <TabsHeader className="bg-gray-100 p-1 m-4 mb-0">
                <Tab value="own" className="flex items-center gap-2">
                  <UserIcon className="h-4 w-4" />
                  My Drafts
                  {ownDrafts.length > 0 && (
                    <Chip size="sm" value={ownDrafts.length} className="ml-1" />
                  )}
                </Tab>
                <Tab value="team" className="flex items-center gap-2">
                  <UserGroupIcon className="h-4 w-4" />
                  Team Drafts
                  {teamDrafts.length > 0 && (
                    <Chip size="sm" value={teamDrafts.length} className="ml-1" />
                  )}
                </Tab>
              </TabsHeader>
              <TabsBody>
                <TabPanel value="own" className="p-0">
                  <DraftsTable drafts={ownDrafts} showPreparer={false} />
                </TabPanel>
                <TabPanel value="team" className="p-0">
                  <DraftsTable drafts={teamDrafts} showPreparer={true} />
                </TabPanel>
              </TabsBody>
            </Tabs>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

export default DraftsListPage;
